import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner } from 'react-bootstrap';
import InternationalPhoneInput from '../../common/InternationalPhoneInput';
import { apiService } from '../../../services/api';
import './Registration.scss';

interface TeamRegistrationProps {
  event: any;
  category: any;
  onSuccess?: (data: any) => void;
  onCancel?: () => void;
}

interface Participant {
  [key: string]: string;
}

const TeamRegistration: React.FC<TeamRegistrationProps> = ({ 
  event, 
  category, 
  onSuccess, 
  onCancel 
}) => {
  const [formData, setFormData] = useState({
    team_name: '',
    email: '',
    phone: '',
    notes: ''
  });
  
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Initialize participants based on team_member_required
  React.useEffect(() => {
    const requiredMembers = category.team_member_required || 3;
    const initialParticipants = Array.from({ length: requiredMembers }, () => ({}));
    setParticipants(initialParticipants);
  }, [category.team_member_required]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData(prev => ({ ...prev, phone: value || '' }));
  };

  const handleParticipantChange = (participantIndex: number, fieldName: string, value: string) => {
    setParticipants(prev => {
      const updated = [...prev];
      updated[participantIndex] = {
        ...updated[participantIndex],
        [fieldName]: value
      };
      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Prepare participants data in the format expected by backend
      const participantsData: { [key: number]: any } = {};
      participants.forEach((participant, index) => {
        participantsData[index + 1] = participant;
      });

      const response = await apiService.registration.team({
        event_slug: event.slug,
        category_id: category.id,
        team_name: formData.team_name,
        email: formData.email,
        phone: formData.phone,
        participants: participantsData,
        notes: formData.notes
      });

      if (response.success) {
        setSuccess(true);
        
        // Dispatch cart update event to notify header
        window.dispatchEvent(new CustomEvent('cartUpdated', {
          detail: { message: 'Cart updated successfully' }
        }));
        
        onSuccess?.(response);
      } else {
        setError(response.error || 'Team registration failed');
      }
    } catch (err: any) {
      setError(err.message || 'Team registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="registration-success">
        <Card className="text-center">
          <Card.Body>
            <div className="success-icon mb-3">
              <i className="fas fa-users text-success" style={{ fontSize: '3rem' }}></i>
            </div>
            <h3 className="text-success mb-3">Team Registration Successful!</h3>
            <p className="text-muted mb-4">
              Your team <strong>{formData.team_name}</strong> has been successfully registered for <strong>{category.name}</strong>.
            </p>
            <p className="text-muted mb-4">
              A confirmation email has been sent to <strong>{formData.email}</strong>.
            </p>
            <Button variant="primary" onClick={onCancel}>
              Close
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="team-registration">
      <Card>
        <Card.Header>
          <h4 className="mb-0">Team Registration</h4>
          <p className="text-muted mb-0">Register your team for {category.name}</p>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Team Information Section */}
            <div className="mb-4">
              <h5 className="mb-3">Team Information</h5>
              <Form.Group className="mb-3">
                <Form.Label className="required">Team Name</Form.Label>
                <Form.Control
                  type="text"
                  name="team_name"
                  value={formData.team_name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your team name"
                  className="simple-input"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="required">Contact Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter contact email address"
                      className="simple-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <InternationalPhoneInput
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="Enter contact phone number"
                      id="phone"
                      name="phone"
                      label="Contact Phone"
                      showLabel={true}
                      required={true}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Team Information</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Provide any additional information about your team"
                  className="simple-input"
                />
              </Form.Group>
            </div>

            {/* Participants Section */}
            <div className="mb-4">
              <h5 className="mb-3">Team Members ({category.team_member_required || 3} required)</h5>
              {participants.map((participant, index) => (
                <div key={index} className="participant-section mb-4 p-3 border rounded">
                  <h6 className="mb-3">Participant {index + 1}</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="required">Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={participant.name || ''}
                          onChange={(e) => handleParticipantChange(index, 'name', e.target.value)}
                          required
                          placeholder="Enter participant name"
                          className="simple-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="required">Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={participant.email || ''}
                          onChange={(e) => handleParticipantChange(index, 'email', e.target.value)}
                          required
                          placeholder="Enter participant email"
                          className="simple-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="tel"
                          value={participant.phone || ''}
                          onChange={(e) => handleParticipantChange(index, 'phone', e.target.value)}
                          placeholder="Enter participant phone"
                          className="simple-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Additional Info</Form.Label>
                        <Form.Control
                          type="text"
                          value={participant.additional_info || ''}
                          onChange={(e) => handleParticipantChange(index, 'additional_info', e.target.value)}
                          placeholder="Any additional information"
                          className="simple-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>

            <div className="registration-actions">
              <Button
                type="button"
                variant="link"
                onClick={onCancel}
                className="btn-cancel"
              >
                <i className="fas fa-times"></i>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Registering Team...
                  </>
                ) : (
                  'Register Team'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default TeamRegistration;
